import React, { useState } from 'react';
import { createInvitation } from '../api/invitationApi';
import logger from '../utils/logger';


const InvitationForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [invitationUrl, setInvitationUrl] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });
    setInvitationUrl('');

    logger.debug('Submitting invitation form with data:', formData);

    try {
      const response = await createInvitation(formData);
      logger.debug('Invitation creation response:', response);

      // If the invitation URL was generated
      if (response.invitationUrl) {
        setInvitationUrl(response.invitationUrl);
        logger.debug('Generated invitation URL:', response.invitationUrl);
      }

      // Log the invitation object
      if (response.invitation) {
        console.log('Created invitation details:', {
          id: response.invitation._id,
          name: response.invitation.name,
          email: response.invitation.email,
          code: response.invitation.code,
          status: response.invitation.status,
          createdAt: response.invitation.createdAt,
          expiresAt: response.invitation.expiresAt
        });
      } else {
        logger.warn('No invitation object in response');
      }

      setMessage({
        type: 'success',
        text: response.message || 'Invitation URL generated successfully!'
      });

      // Clear form
      setFormData({
        name: '',
        email: ''
      });
    } catch (error) {
      logger.error('Error generating invitation URL:', error);
      logger.error('Error response:', error.response);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to generate invitation URL'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Generate Invitation URL</h2>
      
      {message.text && (
        <div className={`p-4 mb-4 rounded-md ${
          message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          {message.text}
        </div>
      )}
      
      {invitationUrl && (
        <div className="p-4 mb-4 bg-yellow-100 text-yellow-800 rounded-md">
          <p className="font-medium">Invitation URL (share manually):</p>
          <div className="mt-2 p-2 bg-gray-100 rounded break-all">
            {invitationUrl}
          </div>
          <button
            className="mt-2 text-sm text-blue-600 hover:text-blue-800"
            onClick={() => {
              navigator.clipboard.writeText(invitationUrl);
              alert('Invitation URL copied to clipboard!');
            }}
          >
            Copy to clipboard
          </button>
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter recipient's name"
            required
          />
        </div>
        
        <div className="mb-4">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter recipient's email"
            required
          />
        </div>
        
        <button
          type="submit"
          disabled={loading}
          className={`w-full py-2 px-4 rounded-md text-white font-medium ${
            loading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Generating...
            </span>
          ) : (
            'Generate Invite URL'
          )}
        </button>
      </form>
    </div>
  );
};

export default InvitationForm;
